import React, { Component } from "react";
import Sidebar from "./common/sidebar_components/sidebar";
import Footer from "./common/footer";
import Header from "./common/header_components/header";
import GlobalLoadingBar from "./common/GlobalLoadingBar";
import { connect } from "react-redux";
import {
  getAllAdminsRedux,
  getAllBookingsRedux,
  getAllRechargeRequestRedux,
  getAllRefundRequestRedux,
  setCurrentAdmin,
  getSingleCashSummaryRedux,
  createMonthRedux,
} from "../actions";
import {
  deleteAllLastMonthUnreceivedApprovedP2p,
  updateAgentStatusIfNotProductReceivedInDay,
} from "../firebase/firebase.utils";
import { initEqualizeReactTableColumns } from "../utils/equalizeReactTableColumns";
import { clearCache } from "../utils/persistentCache";

const MAINTENANCE_STAMP_KEY = "algLastMaintenanceRun";

// The p2p cleanup jobs sweep the previous month, so running them once a day is
// as good as running them on every page load — which is what they did before,
// costing every admin a batch of Firestore writes on every navigation. Errors
// are swallowed deliberately: this is background housekeeping and must never
// surface to whoever happened to open the app first that day.
const runDailyMaintenance = async () => {
  try {
    const today = new Date().toDateString();
    if (localStorage.getItem(MAINTENANCE_STAMP_KEY) === today) return;
    localStorage.setItem(MAINTENANCE_STAMP_KEY, today);
    await deleteAllLastMonthUnreceivedApprovedP2p();
    await updateAgentStatusIfNotProductReceivedInDay();
  } catch (error) {
    console.error("Daily maintenance failed:", error);
  }
};

export class App extends Component {
  // These preloads feed the shell's badges and the lookups pages share (users,
  // lots, admins). They used to run as a chain of `await`s, so the wait was the
  // *sum* of every round trip; fired together it is the slowest one instead.
  // One failure must not strand the rest, so each is caught on its own — a
  // rejected promise inside Promise.all would abandon the others' dispatches.
  componentDidMount = async () => {
    initEqualizeReactTableColumns();

    // Nothing reads the cached copy of the whole `lots` collection any more —
    // the last caller went with the dashboard's Active Shipments tile — but
    // every admin's browser is still holding several megabytes of it, which
    // will now never be refreshed or evicted. Dropped once; a no-op after
    // that, and it never touches the `users` copy, which is still in use.
    clearCache("lots");

    const settle = (promise, name) =>
      Promise.resolve(promise).catch((error) => {
        console.error(`Preload failed: ${name}`, error);
        return null;
      });

    await Promise.all([
      settle(this.props.getSingleCashSummaryRedux(), "cashSummary"),
      settle(this.props.createMonthRedux(), "months"),
      settle(this.props.getAllRechargeRequestRedux(), "rechargeRequests"),
      settle(this.props.getAllRefundRequestRedux("Pending"), "refundRequests"),
      settle(this.props.getAllAdminsRedux(), "admins"),
      // Loading every customer and every lot used to run here, pulling
      // several megabytes on every single page load and holding the page's
      // own query behind it on Firestore's one connection. Neither belongs in
      // the shell: pages now load the customers and lots their rows actually
      // reference (ensureUsersForRowsRedux / ensureLotsRedux), and the few
      // screens that genuinely search the whole list fetch it when someone
      // uses that search. There is no whole-lots read left anywhere on the
      // path an admin walks after login.
      // `getAllPaymentDayRedux()` used to run here too. Nothing in the shell
      // shows payment days — the only consumer is the Payments page, and it
      // already fetches them itself on mount (payments.js), so this was a
      // whole collection read on every page load for data no page was
      // displaying.
      // `getAllInvoiceDueRedux()` used to run here too. It is an unbounded
      // scan of the orders collection — the single most expensive read on
      // boot — and nothing consumed the `ordersAlg.dueInvoices` slice it
      // filled: the dashboard, the only screen that shows the figure, fetches
      // its own copy in its own Promise.all (dashboard.js). So every page load
      // in the app paid for a full orders scan that was then thrown away.
    ]);

    // Housekeeping writes, not page data. They used to be awaited in the same
    // chain, so every admin sat through a Firestore *write* batch before they
    // could work, on every page load. Kicked off unawaited and only once a day
    // per browser — they still run, they just no longer stand between the
    // admin and the page. The proper home for them is a scheduled Cloud
    // Function; this is the change that doesn't require standing one up.
    runDailyMaintenance();
  };
  render() {
    return (
      <div className="page-wrapper">
        {/* Shows whenever any Firestore read is in flight, on every page,
            without blocking the one underneath. */}
        <GlobalLoadingBar />
        <Header />
        <div className="page-body-wrapper">
          <Sidebar />
          {/* The shell and the route render straight away. This used to be
              covered by an opaque, click-blocking FullPageLoader until every
              preload above had finished — so the page an admin actually asked
              for was hidden behind data it mostly doesn't use (sidebar badge
              counts, shared lookups). Each page already shows its own loading
              state for its own data. */}
          <div className="page-body">{this.props.children}</div>
          <Footer />
        </div>
      </div>
    );
  }
}
const mapStateToProps = (state) => {
  return {
    admins: state.admins.admins,
    currentAdmin: state.admins.currentAdmin,
    // `allBookingRequest`/`allRefundRequest`/`allRechargeRequest` used to be
    // returned here as fresh `[]` literals. Nothing in this component read
    // them, but a new array identity on every dispatch made connect re-render
    // the whole app shell — header, sidebar and the current page — on every
    // single action in the app.
  };
};
export default connect(mapStateToProps, {
  getAllAdminsRedux,
  getAllBookingsRedux,
  getAllRechargeRequestRedux,
  getAllRefundRequestRedux,
  setCurrentAdmin,
  getSingleCashSummaryRedux,
  createMonthRedux,
})(App);
